import { useEffect, useRef, useState } from 'react';
import { usePublicSiteSettings } from '@/hooks/usePublicSiteSettings';
import { cn } from '@/lib/utils';

interface Stat {
  value: number;
  label: string;
  suffix?: string;
}

interface AnimatedCounterProps {
  end: number;
  duration?: number;
  suffix?: string;
  shouldAnimate: boolean;
}

function AnimatedCounter({ end, duration = 2000, suffix = '', shouldAnimate }: AnimatedCounterProps) {
  const [count, setCount] = useState(0);
  const animationRef = useRef<number | null>(null);
  const startTimeRef = useRef<number | null>(null);
  const hasAnimated = useRef(false);

  useEffect(() => {
    if (!shouldAnimate || hasAnimated.current) return;
    
    hasAnimated.current = true;
    startTimeRef.current = null;

    const animate = (timestamp: number) => {
      if (!startTimeRef.current) {
        startTimeRef.current = timestamp;
      }

      const progress = Math.min((timestamp - startTimeRef.current) / duration, 1);
      
      // Easing function - easeOutQuart for smooth deceleration
      const easeOutQuart = 1 - Math.pow(1 - progress, 4);
      
      setCount(Math.floor(easeOutQuart * end));

      if (progress < 1) {
        animationRef.current = requestAnimationFrame(animate);
      } else {
        setCount(end);
      }
    };

    animationRef.current = requestAnimationFrame(animate);

    return () => {
      if (animationRef.current) {
        cancelAnimationFrame(animationRef.current);
      }
    };
  }, [shouldAnimate, end, duration]);

  return (
    <span>
      + {count}{suffix}
    </span>
  );
}

interface AnimatedStatsProps {
  className?: string;
}

export function AnimatedStats({ className }: AnimatedStatsProps) {
  const { data: settings } = usePublicSiteSettings();
  const [isVisible, setIsVisible] = useState(false);
  const sectionRef = useRef<HTMLDivElement>(null);

  const stats: Stat[] = [
    { value: 7, label: 'anos no mercado' },
    { value: 700, label: 'atendimentos realizados' },
    { value: 350, label: 'negociações concluídas' },
    { value: 200, label: 'milhões em VGV', suffix: 'M' },
  ];

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
          observer.disconnect();
        }
      },
      {
        threshold: 0.3,
        rootMargin: '0px 0px -50px 0px',
      }
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => observer.disconnect();
  }, []);

  return (
    <section 
      ref={sectionRef}
      className={cn('py-10 md:py-16 lg:py-20 bg-background border-y border-border', className)}
    >
      <div className="container-custom px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-2 gap-6 md:grid-cols-4 md:gap-4">
          {stats.map((stat, index) => (
            <div 
              key={index}
              className={cn(
                'relative text-center transition-all duration-700',
                isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4',
                // Divider between items on desktop
                index < stats.length - 1 && 'md:border-r md:border-border'
              )}
              style={{ transitionDelay: `${index * 150}ms` }}
            >
              <p className="font-display text-2xl sm:text-3xl md:text-4xl lg:text-5xl xl:text-6xl font-bold text-foreground">
                <AnimatedCounter 
                  end={stat.value} 
                  suffix={stat.suffix} 
                  shouldAnimate={isVisible}
                  duration={2000 + index * 200}
                />
              </p>
              <p className="mt-1 md:mt-2 text-xs sm:text-sm md:text-base text-muted-foreground leading-tight">
                {stat.label}
              </p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
